/**
 * MyReferences — Feb 2026
 * =======================
 *
 * The teen's personal Reference Library: everything they tapped "Save"
 * on inside the in-chat AvA Reference panel (see
 * `AvAContextDisplay.jsx`). Reads from `GET /api/chat/context/mine`
 * (implemented in `/app/backend/app/api/chat_context.py`).
 *
 * Grid of link/image cards, newest first, capped at 100 by the API.
 * No admin flavour — this is 1:1 the teen's saved artifacts, so a
 * light card + hostname header + open-in-new-tab / download pattern
 * matches the AvA Reference box exactly.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookMarked, ExternalLink, ImageIcon, Loader2, Sparkles,
} from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';

const hostnameOf = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
};

const MyReferences = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiClient.request('/api/chat/context/mine');
        if (!cancelled) setItems(data?.items || []);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Could not load your library.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="my-references-page">
        <div className="mb-6 flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
               style={{ background: '#EDE9FE', color: '#5B21B6' }}>
            <BookMarked size={22} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              My Reference Library
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Everything you saved from a chat — links, images and examples your AvAs
              shared. Tap any card to reopen it. Newest first.
            </p>
          </div>
        </div>

        {loading && (
          <div className="py-24 flex items-center justify-center" data-testid="my-references-loading">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        )}

        {!loading && error && (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm"
               data-testid="my-references-error">
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="py-16 text-center" data-testid="my-references-empty">
            <div className="mx-auto w-14 h-14 rounded-full bg-purple-50 flex items-center justify-center mb-3">
              <Sparkles className="text-purple-400" size={22} />
            </div>
            <p className="text-base font-semibold text-gray-800 mb-1">
              Nothing saved yet
            </p>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              While chatting with your AvA, tap the <b>Save</b> icon on any
              reference (a link or image they share) — it will show up here for
              you to revisit any time.
            </p>
            <Link
              to="/conversations"
              className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 transition"
              data-testid="my-references-empty-cta"
            >
              Go to Practice <ExternalLink size={14} />
            </Link>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
               data-testid="my-references-grid">
            {items.map((it) => (
              <a
                key={it.id}
                href={it.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-2xl border-2 border-purple-100 bg-white hover:border-purple-400 hover:shadow-lg transition overflow-hidden flex flex-col"
                data-testid={`my-reference-item-${it.id}`}
              >
                <div className="aspect-video bg-gradient-to-br from-purple-50 to-slate-100 flex items-center justify-center overflow-hidden">
                  {it.kind === 'image' ? (
                    <img
                      src={it.url}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-purple-500">
                      <ExternalLink size={26} />
                      <span className="text-[11px] font-bold uppercase tracking-wider">Link</span>
                    </div>
                  )}
                </div>
                <div className="p-3 flex-1 flex flex-col gap-1">
                  <div className="flex items-center gap-1.5">
                    {it.kind === 'image'
                      ? <ImageIcon size={12} className="text-purple-500" />
                      : <ExternalLink size={12} className="text-purple-500" />}
                    <span className="text-[10px] font-bold uppercase tracking-wider text-purple-700 truncate">
                      {hostnameOf(it.url)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-800 leading-snug break-words line-clamp-2">
                    {it.url}
                  </div>
                  <div className="mt-auto pt-2 flex items-center justify-between text-[11px] text-gray-500">
                    <span>{it.ava_name ? `Shared by ${it.ava_name}` : 'From your chat'}</span>
                    <span>{formatDate(it.last_saved_at || it.created_at)}</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MyReferences;
